import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { Generic, GenericCompartimentSearch } from 'src/app/interfaces/generic.interface';
import { UserOrganigramaSearchPayload } from 'src/app/interfaces/common.interface';
import { CommonService } from 'src/app/services/common.service';
import { OperatorAccess } from 'src/app/platform/nomenclatoare/operatori/operatori.interface';
import { OperatoriService } from 'src/app/platform/nomenclatoare/operatori/operatori.service';
import { ActivityFiltersPayload } from './activity-filters.interface';

@Component({
    selector: 'app-activity-filters',
    templateUrl: './activity-filters.component.html',
    styleUrls: ['./activity-filters.component.scss'],
})
export class ActivityFiltersComponent implements OnInit, OnDestroy {
    @Input() initialPayload?: Partial<ActivityFiltersPayload>;
    @Input() showDocumentNumber = true;
    @Input() showRegistruType = true;
    @Input() showRegistruSerie = true;
    /** Raport cu acces admin: listează TOȚI operatorii direcției/compartimentului, nu doar responsabilul. */
    @Input() viewAllOperators = false;

    @Output() search = new EventEmitter<ActivityFiltersPayload>();
    @Output() reset = new EventEmitter<void>();

    today = new Date();
    isLoading = false;

    registreActiveSwitch: Generic[] = [];
    categoriiActive: Generic[] = [];
    surseActive: Generic[] = [];
    userAccesses: OperatorAccess[] = [];
    activeAccess: OperatorAccess | null = null;

    activityStatusOptions: { id: string; name: string }[] = [
        { id: 'in_lucru', name: 'În lucru' },
        { id: 'finalizat', name: 'Finalizat' },
        { id: 'nepreluat', name: 'Nepreluat' },
        { id: 'invalidat', name: 'Invalidat' },
    ];

    termStatusOptions: { id: string; name: string }[] = [
        { id: 'in_termen', name: 'În termen (≥10 zile)' },
        { id: 'atentie', name: 'Necesită atenție (5–9 zile)' },
        { id: 'urgent', name: 'Urgent / Depășit (<5 zile)' },
    ];

    tipDocumentControl = new FormControl();
    filteredTipuriDocument: Generic[] = [];
    isTipDocumentLoading = false;

    tipRapoarte: { name: string; id: number }[] = [
        { name: '(Registru) General/Global', id: 1 },
        { name: '(Registru) Tipuri de documente', id: 2 },
        { name: 'Destinatie servicii - general', id: 3 },
        { name: 'Destinatie servicii - tipuri documente', id: 4 },
    ];

    form: FormGroup = new FormGroup({
        origin_directie_id: new FormControl(null),
        origin_directie_name: new FormControl(''),
        origin_compartiment_id: new FormControl(null),
        origin_compartiment_name: new FormControl(''),
        origin_user_id: new FormControl(-1),
        origin_user_name: new FormControl(''),

        destinatie_directie_id: new FormControl(null),
        destinatie_directie_name: new FormControl(''),
        destinatie_compartiment_id: new FormControl(null),
        destinatie_compartiment_name: new FormControl(''),
        destinatie_user_id: new FormControl(-1),
        destinatie_user_name: new FormControl(''),

        document_number: new FormControl(null),
        date_input_start: new FormControl(''),
        date_input_end: new FormControl(''),
        id_serie: new FormControl(-1),
        registru_type: new FormControl(1),
        id_categorii: new FormControl<number[]>([]),
        source_id: new FormControl(-1),
        tip_document_id: new FormControl(-1),
        tip_document_name: new FormControl(''),
        activity_status: new FormControl<string[]>([]),
        term_status: new FormControl<string[]>([]),
    });

    private destroy$ = new Subject<void>();

    directiiOrigin: Generic[] = [];
    filteredDirectiiOrigin: Generic[] = [];
    directiiOriginControl = new FormControl();

    compartimenteOrigin: GenericCompartimentSearch[] = [];
    compartimenteOriginControl = new FormControl();
    isCompartimenteOriginLoading = false;
    showCompartimenteOriginMessage = true;
    compartimenteOriginLoadComplete = false;

    operatoriOrigin: Generic[] = [];
    filteredOperatoriOrigin: Generic[] = [];
    operatoriOriginControl = new FormControl();

    directiiDestinatie: Generic[] = [];
    filteredDirectiiDestinatie: Generic[] = [];
    directiiDestinatieControl = new FormControl();

    compartimenteDestinatie: GenericCompartimentSearch[] = [];
    compartimenteDestinatieControl = new FormControl();
    isCompartimenteDestinatieLoading = false;
    showCompartimenteDestinatieMessage = true;
    compartimenteDestinatieLoadComplete = false;

    operatoriDestinatie: Generic[] = [];
    filteredOperatoriDestinatie: Generic[] = [];
    operatoriDestinatieControl = new FormControl();

    constructor(
        private commonService: CommonService,
        private operatoriService: OperatoriService,
    ) {}

    ngOnInit() {
        this.loadDirectiiActive();
        this.loadUserAccess();
        this.loadActiveSwitch();
        this.loadCategoriiActive();
        this.loadSurseActive();

        this.tipDocumentControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((val) => {
                    if (typeof val !== 'string' || val.trim().length < 2) {
                        this.isTipDocumentLoading = false;
                        return of([] as Generic[]);
                    }
                    this.isTipDocumentLoading = true;
                    return this.commonService.searchTipDocumente(val).pipe(catchError(() => of([] as Generic[])));
                }),
                tap((list: Generic[]) => {
                    this.filteredTipuriDocument = list;
                    this.isTipDocumentLoading = false;
                }),
            )
            .subscribe();

        this.compartimenteOriginControl.valueChanges.pipe(debounceTime(600)).subscribe((compartiment) => {
            if (typeof compartiment === 'string') {
                this.fetchCompartimenteOrigin(compartiment);
                this.showCompartimenteOriginMessage = true;
                this.compartimenteOriginLoadComplete = false;
            }
        });

        this.compartimenteDestinatieControl.valueChanges.pipe(debounceTime(600)).subscribe((compartiment) => {
            if (typeof compartiment === 'string') {
                this.fetchCompartimenteDestinatie(compartiment);
                this.showCompartimenteDestinatieMessage = true;
                this.compartimenteDestinatieLoadComplete = false;
            }
        });

        this.renderFieldsSearch();

        if (this.initialPayload) {
            this.applyInitialPayload(this.initialPayload);
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private applyInitialPayload(p: Partial<ActivityFiltersPayload>): void {
        const o = p.origin;
        const d = p.destinatie;
        const i = p.info;

        if (o) {
            if (o.directie_id) {
                this.directiiOriginControl.setValue({ id: o.directie_id, name: o.directie_name || '' });
                this.form.patchValue({ origin_directie_id: o.directie_id, origin_directie_name: o.directie_name });
            }
            if (o.compartiment_id) {
                this.compartimenteOriginControl.setValue({ id: o.compartiment_id, name: o.compartiment_name || '' });
                this.form.patchValue({ origin_compartiment_id: o.compartiment_id, origin_compartiment_name: o.compartiment_name });
            }
            if (o.user_id) {
                this.form.patchValue({ origin_user_id: o.user_id, origin_user_name: o.user_name });
            }
        }
        if (d) {
            if (d.directie_id) {
                this.directiiDestinatieControl.setValue({ id: d.directie_id, name: d.directie_name || '' });
                this.form.patchValue({ destinatie_directie_id: d.directie_id, destinatie_directie_name: d.directie_name });
            }
            if (d.compartiment_id) {
                this.compartimenteDestinatieControl.setValue({ id: d.compartiment_id, name: d.compartiment_name || '' });
                this.form.patchValue({ destinatie_compartiment_id: d.compartiment_id, destinatie_compartiment_name: d.compartiment_name });
            }
            if (d.user_id) {
                this.form.patchValue({ destinatie_user_id: d.user_id, destinatie_user_name: d.user_name });
            }
        }
        if (i) {
            this.form.patchValue({
                document_number: i.number,
                date_input_start: i.date_input_start ? new Date(i.date_input_start) : '',
                date_input_end: i.date_input_end ? new Date(i.date_input_end) : '',
                id_serie: i.id_serie ?? -1,
                registru_type: i.registru_type ?? 1,
                id_categorii: Array.isArray(i.id_categorii) ? i.id_categorii : [],
                source_id: i.source_id ?? -1,
                tip_document_id: i.tip_document_id ?? -1,
                activity_status: Array.isArray(i.activity_status) ? i.activity_status : [],
                term_status: Array.isArray(i.term_status) ? i.term_status : [],
            });
            if (i.tip_document_id) {
                // setăm un obiect (nu string) ca blur-ul autocomplete-ului să nu reseteze filtrul
                this.tipDocumentControl.setValue({ id: i.tip_document_id, name: '' });
            }
        }
    }

    loadUserAccess(): void {
        this.operatoriService.userAccess$.pipe(takeUntil(this.destroy$)).subscribe((userAccesses) => {
            this.userAccesses = userAccesses;
            this.activeAccess = this.userAccesses.find((access) => access.active) || null;

            if (this.activeAccess && !this.initialPayload?.origin?.directie_id) {
                this.directiiOriginControl.setValue({
                    id: this.activeAccess.id_directie,
                    name: this.activeAccess.denumire_directie,
                });
                this.form.patchValue({
                    origin_directie_id: this.activeAccess.id_directie,
                    origin_directie_name: this.activeAccess.denumire_directie,
                });

                this.compartimenteOriginControl.setValue({
                    id: this.activeAccess.id_compartiment,
                    name: this.activeAccess.denumire_compartiment,
                });
                this.form.patchValue({
                    origin_compartiment_id: this.activeAccess.id_compartiment,
                    origin_compartiment_name: this.activeAccess.denumire_compartiment,
                });

                this.loadOperatoriOrigin();
            }
        });
    }

    loadActiveSwitch() {
        this.isLoading = true;
        this.commonService
            .getActiveSwitch()
            .pipe(
                map((resp: Generic[]) => [...resp]),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (results) => {
                    this.registreActiveSwitch = results;
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                },
            });
    }

    loadCategoriiActive() {
        this.commonService
            .getTipuriAccesActive()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (results: Generic[]) => {
                    this.categoriiActive = results;
                },
                error: (error) => {
                    console.error('Error loading categorii', error);
                    this.categoriiActive = [];
                },
            });
    }

    loadSurseActive() {
        this.commonService
            .getSurseActive()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (results: Generic[]) => {
                    this.surseActive = results;
                },
                error: (error) => {
                    console.error('Error loading surse', error);
                    this.surseActive = [];
                },
            });
    }

    onTipDocumentSelected(item: Generic): void {
        this.form.patchValue({ tip_document_id: item.id, tip_document_name: item.name });
    }

    onTipDocumentBlur(): void {
        if (typeof this.tipDocumentControl.value === 'string') {
            this.tipDocumentControl.setValue('');
            this.filteredTipuriDocument = [];
            this.form.patchValue({ tip_document_id: -1, tip_document_name: '' });
        }
    }

    renderFieldsSearch() {
        this.directiiOriginControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged(),
                debounceTime(150),
                switchMap((inputValue) => {
                    if (typeof inputValue !== 'string') return of(this.directiiOrigin);
                    if (inputValue.length < 1) {
                        this.filteredDirectiiOrigin = this.directiiOrigin;
                        return of(this.directiiOrigin);
                    }
                    return this.filterListByName(this.directiiOrigin, inputValue);
                }),
                tap((data: Generic[]) => (this.filteredDirectiiOrigin = data)),
                catchError(() => of([])),
            )
            .subscribe();

        this.operatoriOriginControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged(),
                debounceTime(150),
                switchMap((inputValue) => {
                    if (typeof inputValue !== 'string') return of(this.operatoriOrigin);
                    if (inputValue.length < 1) {
                        this.filteredOperatoriOrigin = this.operatoriOrigin;
                        return of(this.operatoriOrigin);
                    }
                    return this.filterListByName(this.operatoriOrigin, inputValue);
                }),
                tap((data: Generic[]) => (this.filteredOperatoriOrigin = data)),
                catchError(() => of([])),
            )
            .subscribe();

        this.directiiDestinatieControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged(),
                debounceTime(150),
                switchMap((inputValue) => {
                    if (typeof inputValue !== 'string') return of(this.directiiDestinatie);
                    if (inputValue.length < 1) {
                        this.filteredDirectiiDestinatie = this.directiiDestinatie;
                        return of(this.directiiDestinatie);
                    }
                    return this.filterListByName(this.directiiDestinatie, inputValue);
                }),
                tap((data: Generic[]) => (this.filteredDirectiiDestinatie = data)),
                catchError(() => of([])),
            )
            .subscribe();

        this.operatoriDestinatieControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged(),
                debounceTime(150),
                switchMap((inputValue) => {
                    if (typeof inputValue !== 'string') return of(this.operatoriDestinatie);
                    if (inputValue.length < 1) {
                        this.filteredOperatoriDestinatie = this.operatoriDestinatie;
                        return of(this.operatoriDestinatie);
                    }
                    return this.filterListByName(this.operatoriDestinatie, inputValue);
                }),
                tap((data: Generic[]) => (this.filteredOperatoriDestinatie = data)),
                catchError(() => of([])),
            )
            .subscribe();
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: Generic[]) => [...results]),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (results) => {
                    this.directiiOrigin = results;
                    this.directiiDestinatie = results;
                    this.filteredDirectiiOrigin = [...this.directiiOrigin];
                    this.filteredDirectiiDestinatie = [...this.directiiDestinatie];
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                    this.filteredDirectiiOrigin = [];
                    this.filteredDirectiiDestinatie = [];
                },
            });
    }

    onDirectiiOriginChange(selectedDirectie: Generic): void {
        this.form.patchValue({
            origin_directie_id: selectedDirectie.id,
            origin_directie_name: selectedDirectie.name,
        });
        this.compartimenteOriginControl.patchValue('');
        this.form.patchValue({ origin_compartiment_id: null, origin_compartiment_name: '' });
        this.operatoriOrigin = [];
        this.filteredOperatoriOrigin = [];
        this.operatoriOriginControl.patchValue('');
        this.form.patchValue({ origin_user_id: -1, origin_user_name: '' });
    }

    displayGeneric(item: Generic | string | null): string {
        return item && typeof item === 'object' && item.name ? item.name : '';
    }

    onDirectiiOriginBlur() {
        if (typeof this.directiiOriginControl.value === 'string') {
            this.directiiOriginControl.patchValue('');
            this.form.patchValue({
                origin_directie_id: null,
                origin_directie_name: '',
                origin_compartiment_id: null,
                origin_compartiment_name: '',
                origin_user_id: -1,
                origin_user_name: '',
            });
            this.compartimenteOriginControl.patchValue('');
            this.operatoriOrigin = [];
            this.filteredOperatoriOrigin = [];
            this.operatoriOriginControl.patchValue('');
        }
    }

    fetchCompartimenteOrigin(compartiment: string) {
        if (compartiment.length >= 2) {
            this.isCompartimenteOriginLoading = true;
            this.commonService.searchCompartimenteActive(undefined, compartiment).subscribe({
                next: (response: GenericCompartimentSearch[]) => {
                    this.compartimenteOrigin = response;
                    this.isCompartimenteOriginLoading = false;
                    this.compartimenteOriginLoadComplete = true;
                    this.showCompartimenteOriginMessage = this.compartimenteOrigin.length === 0;
                },
                error: (err: unknown) => {
                    this.isCompartimenteOriginLoading = false;
                    this.showCompartimenteOriginMessage = true;
                    this.compartimenteOriginLoadComplete = true;
                    console.log(err);
                },
            });
        } else {
            this.compartimenteOrigin = [];
            this.showCompartimenteOriginMessage = true;
            this.compartimenteOriginLoadComplete = false;
        }
    }

    onCompartimenteOriginSelected(selectedCompartiment: GenericCompartimentSearch): void {
        this.form.patchValue({
            origin_compartiment_id: selectedCompartiment.id,
            origin_compartiment_name: selectedCompartiment.name,
        });
        this.directiiOriginControl.setValue({
            id: selectedCompartiment.directie_id,
            name: selectedCompartiment.directie_name,
        });
        this.form.patchValue({
            origin_directie_id: selectedCompartiment.directie_id,
            origin_directie_name: selectedCompartiment.directie_name,
        });
        this.loadOperatoriOrigin();
        this.operatoriOriginControl.patchValue('');
        this.form.patchValue({ origin_user_id: -1, origin_user_name: '' });
    }

    onCompartimenteOriginBlur() {
        if (typeof this.compartimenteOriginControl.value === 'string') {
            this.compartimenteOriginControl.patchValue('');
            this.form.patchValue({
                origin_compartiment_id: null,
                origin_compartiment_name: '',
                origin_user_id: -1,
                origin_user_name: '',
            });
            this.operatoriOrigin = [];
            this.filteredOperatoriOrigin = [];
            this.operatoriOriginControl.patchValue('');
        }
    }

    loadOperatoriOrigin() {
        this.isLoading = true;
        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.form.get('origin_directie_id')?.value,
            compartiment_id: this.form.get('origin_compartiment_id')?.value,
            view_all: this.viewAllOperators ? 1 : 0,
        };
        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: Generic[]) => [...results]),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (data) => {
                    this.operatoriOrigin = [...data];
                    this.filteredOperatoriOrigin = [...this.operatoriOrigin];
                },
                error: (error) => {
                    console.error(error);
                    this.operatoriOrigin = [];
                    this.filteredOperatoriOrigin = [];
                },
            });
    }

    onOperatoriOriginChange(operatorId: number): void {
        const selected = this.operatoriOrigin.find((o) => o.id === operatorId);
        if (selected) {
            this.form.patchValue({ origin_user_name: selected.name });
        }
    }

    onDirectiiDestinatieChange(selectedDirectie: Generic): void {
        this.form.patchValue({
            destinatie_directie_id: selectedDirectie.id,
            destinatie_directie_name: selectedDirectie.name,
        });
        this.compartimenteDestinatieControl.patchValue('');
        this.form.patchValue({ destinatie_compartiment_id: null, destinatie_compartiment_name: '' });
        this.operatoriDestinatie = [];
        this.filteredOperatoriDestinatie = [];
        this.operatoriDestinatieControl.patchValue('');
        this.form.patchValue({ destinatie_user_id: -1, destinatie_user_name: '' });
    }

    onDirectiiDestinatieBlur() {
        if (typeof this.directiiDestinatieControl.value === 'string') {
            this.directiiDestinatieControl.patchValue('');
            this.form.patchValue({
                destinatie_directie_id: null,
                destinatie_directie_name: '',
                destinatie_compartiment_id: null,
                destinatie_compartiment_name: '',
                destinatie_user_id: -1,
                destinatie_user_name: '',
            });
            this.compartimenteDestinatieControl.patchValue('');
            this.operatoriDestinatie = [];
            this.filteredOperatoriDestinatie = [];
            this.operatoriDestinatieControl.patchValue('');
        }
    }

    fetchCompartimenteDestinatie(compartiment: string) {
        if (compartiment.length >= 2) {
            this.isCompartimenteDestinatieLoading = true;
            this.commonService.searchCompartimenteActive(undefined, compartiment).subscribe({
                next: (response: GenericCompartimentSearch[]) => {
                    this.compartimenteDestinatie = response;
                    this.isCompartimenteDestinatieLoading = false;
                    this.compartimenteDestinatieLoadComplete = true;
                    this.showCompartimenteDestinatieMessage = this.compartimenteDestinatie.length === 0;
                },
                error: (err: unknown) => {
                    this.isCompartimenteDestinatieLoading = false;
                    this.showCompartimenteDestinatieMessage = true;
                    this.compartimenteDestinatieLoadComplete = true;
                    console.log(err);
                },
            });
        } else {
            this.compartimenteDestinatie = [];
            this.showCompartimenteDestinatieMessage = true;
            this.compartimenteDestinatieLoadComplete = false;
        }
    }

    onCompartimenteDestinatieSelected(selectedCompartiment: GenericCompartimentSearch): void {
        this.form.patchValue({
            destinatie_compartiment_id: selectedCompartiment.id,
            destinatie_compartiment_name: selectedCompartiment.name,
        });
        this.directiiDestinatieControl.setValue({
            id: selectedCompartiment.directie_id,
            name: selectedCompartiment.directie_name,
        });
        this.form.patchValue({
            destinatie_directie_id: selectedCompartiment.directie_id,
            destinatie_directie_name: selectedCompartiment.directie_name,
        });
        this.loadOperatoriDestinatie();
        this.operatoriDestinatieControl.patchValue('');
        this.form.patchValue({ destinatie_user_id: -1, destinatie_user_name: '' });
    }

    onCompartimenteDestinatieBlur() {
        if (typeof this.compartimenteDestinatieControl.value === 'string') {
            this.compartimenteDestinatieControl.patchValue('');
            this.form.patchValue({
                destinatie_compartiment_id: null,
                destinatie_compartiment_name: '',
                destinatie_user_id: -1,
                destinatie_user_name: '',
            });
            this.operatoriDestinatie = [];
            this.filteredOperatoriDestinatie = [];
            this.operatoriDestinatieControl.patchValue('');
        }
    }

    loadOperatoriDestinatie() {
        this.isLoading = true;
        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.form.get('destinatie_directie_id')?.value,
            compartiment_id: this.form.get('destinatie_compartiment_id')?.value,
            view_all: this.viewAllOperators ? 1 : 0,
        };
        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: Generic[]) => [...results]),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (data) => {
                    this.operatoriDestinatie = [...data];
                    this.filteredOperatoriDestinatie = [...this.operatoriDestinatie];
                },
                error: (error) => {
                    console.error(error);
                    this.operatoriDestinatie = [];
                    this.filteredOperatoriDestinatie = [];
                },
            });
    }

    onOperatoriDestinatieChange(operatorId: number): void {
        const selected = this.operatoriDestinatie.find((o) => o.id === operatorId);
        if (selected) {
            this.form.patchValue({ destinatie_user_name: selected.name });
        }
    }

    private normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    }

    private filterListByName<T extends { name: string }>(list: T[], term: string): Observable<T[]> {
        const t = this.normalizeString(term);
        return of(list.filter((x) => this.normalizeString(x.name).includes(t)));
    }

    private normalizeCategoriiValue(raw: unknown): number[] | null {
        if (!Array.isArray(raw) || raw.length === 0) return null;
        const cleaned = raw
            .map((v) => Number(v))
            .filter((n) => Number.isFinite(n) && n > 0);
        return cleaned.length ? cleaned : null;
    }

    private normalizeActivityStatus(raw: unknown): string[] | null {
        if (!Array.isArray(raw) || raw.length === 0) return null;
        const allowed = ['in_lucru', 'finalizat', 'nepreluat', 'invalidat'];
        const cleaned = raw.map((v) => String(v)).filter((v) => allowed.includes(v));
        return cleaned.length ? cleaned : null;
    }

    private normalizeTermStatus(raw: unknown): string[] | null {
        if (!Array.isArray(raw) || raw.length === 0) return null;
        const allowed = ['in_termen', 'atentie', 'urgent'];
        const cleaned = raw.map((v) => String(v)).filter((v) => allowed.includes(v));
        return cleaned.length ? cleaned : null;
    }

    private normalizeSingleId(raw: unknown): number | null {
        return raw === -1 || raw === null || raw === undefined ? null : Number(raw);
    }

    generatePayload(): ActivityFiltersPayload {
        const dateStart = this.form.value.date_input_start;
        const dateEnd = this.form.value.date_input_end;

        return {
            origin: {
                directie_id: this.form.get('origin_directie_id')?.value,
                directie_name: this.form.get('origin_directie_name')?.value || '',
                compartiment_id: this.form.get('origin_compartiment_id')?.value,
                compartiment_name: this.form.get('origin_compartiment_name')?.value || '',
                user_id:
                    this.form.get('origin_user_id')?.value === -1 || this.form.get('origin_user_id')?.value === null
                        ? null
                        : this.form.get('origin_user_id')?.value,
                user_name: this.form.get('origin_user_name')?.value || '',
            },
            destinatie: {
                directie_id: this.form.get('destinatie_directie_id')?.value,
                directie_name: this.form.get('destinatie_directie_name')?.value || '',
                compartiment_id: this.form.get('destinatie_compartiment_id')?.value,
                compartiment_name: this.form.get('destinatie_compartiment_name')?.value || '',
                user_id:
                    this.form.get('destinatie_user_id')?.value === -1 || this.form.get('destinatie_user_id')?.value === null
                        ? null
                        : this.form.get('destinatie_user_id')?.value,
                user_name: this.form.get('destinatie_user_name')?.value || '',
            },
            info: {
                number: this.form.get('document_number')?.value || null,
                date_input_start: dateStart ? formatDate(dateStart, 'yyyy-MM-dd', 'en-US') : '',
                date_input_end: dateEnd ? formatDate(dateEnd, 'yyyy-MM-dd', 'en-US') : '',
                id_serie:
                    this.form.get('id_serie')?.value === -1 || this.form.get('id_serie')?.value === null
                        ? null
                        : this.form.get('id_serie')?.value,
                registru_type: this.form.get('registru_type')?.value ?? null,
                id_categorii: this.normalizeCategoriiValue(this.form.get('id_categorii')?.value),
                source_id: this.normalizeSingleId(this.form.get('source_id')?.value),
                tip_document_id: this.normalizeSingleId(this.form.get('tip_document_id')?.value),
                activity_status: this.normalizeActivityStatus(this.form.get('activity_status')?.value),
                term_status: this.normalizeTermStatus(this.form.get('term_status')?.value),
            },
        };
    }

    onSearch() {
        this.search.emit(this.generatePayload());
    }

    onReset() {
        this.form.patchValue({
            destinatie_directie_id: null,
            destinatie_directie_name: '',
            destinatie_compartiment_id: null,
            destinatie_compartiment_name: '',
            destinatie_user_id: -1,
            destinatie_user_name: '',
            document_number: null,
            date_input_start: '',
            date_input_end: '',
            id_serie: -1,
            registru_type: 1,
            id_categorii: [],
            source_id: -1,
            tip_document_id: -1,
            tip_document_name: '',
            activity_status: [],
            term_status: [],
        });
        this.directiiDestinatieControl.setValue('');
        this.compartimenteDestinatie = [];
        this.compartimenteDestinatieControl.setValue('');
        this.operatoriDestinatie = [];
        this.filteredOperatoriDestinatie = [];
        this.operatoriDestinatieControl.setValue('');
        this.tipDocumentControl.setValue('');
        this.filteredTipuriDocument = [];
        this.reset.emit();
    }

    /** Golește DOAR zona de origine (pentru raport doar pe destinatar). Nu emite reset / nu golește rezultatele. */
    onResetOrigin() {
        this.form.patchValue({
            origin_directie_id: null,
            origin_directie_name: '',
            origin_compartiment_id: null,
            origin_compartiment_name: '',
            origin_user_id: -1,
            origin_user_name: '',
        });
        this.directiiOriginControl.setValue('');
        this.compartimenteOrigin = [];
        this.compartimenteOriginControl.setValue('');
        this.operatoriOrigin = [];
        this.filteredOperatoriOrigin = [];
        this.operatoriOriginControl.setValue('');
    }

    /** Golește DOAR zona de destinatar (pentru raport doar pe origine). Nu emite reset / nu golește rezultatele. */
    onResetDestinatie() {
        this.form.patchValue({
            destinatie_directie_id: null,
            destinatie_directie_name: '',
            destinatie_compartiment_id: null,
            destinatie_compartiment_name: '',
            destinatie_user_id: -1,
            destinatie_user_name: '',
        });
        this.directiiDestinatieControl.setValue('');
        this.compartimenteDestinatie = [];
        this.compartimenteDestinatieControl.setValue('');
        this.operatoriDestinatie = [];
        this.filteredOperatoriDestinatie = [];
        this.operatoriDestinatieControl.setValue('');
    }
}
